import React, { useEffect } from "react";
import { Pin } from "lucide-react";
import { useModalDialog } from "../../../hooks/useModalDialog";
import { usePresence } from "../../../hooks/usePresence";
import { OVERLAY_EXIT_MS } from "../../../design-system/tokens/overlayMotion";
import { SOON_TOOLTIP } from "../../atoms/SoonBadge";
import { Divider, ModalDrawer, DisabledItem, Footer, Heading, ItemLink, PanelHead, PanelLabel, PeekDrawer, PinButton, RowTooltip, Scrim, Soon } from "./NavDrawer.styles";
import { NAV_ICON_SIZE, NAV_ICON_STROKE } from "./NavDrawer.constants";
import type { NavDrawerProps } from "./NavDrawer.types";

/**
 * Navigation drawer. `modal`: overlay that closes on the scrim, Escape, or choosing a link. `peek`: a hover
 * preview over the page (Escape closes it) that can dock into the shelf. Pending items are disabled and tagged.
 */
export const NavDrawer: React.FC<NavDrawerProps> = ({
  open,
  onClose,
  label,
  sections,
  soonLabel,
  footer,
  className,
  variant = "modal",
  docked = false,
  onPinToggle,
  pinLabel,
  onPointerEnter,
  onPointerLeave,
}) => {
  const peek = variant === "peek";
  const dialog = useModalDialog(open && !peek, onClose);
  // A preview takes no focus, so Escape is heard on the document; a docked drawer ignores it.
  // The modal drawer also hears Escape on the document when focus has not moved inside it.
  useEffect(() => {
    if (!open || (peek && docked)) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [peek, open, docked, onClose]);
  const { mounted } = usePresence(open && !peek, OVERLAY_EXIT_MS);
  if (!peek && !mounted) return null;
  const closing = !open;

  const rows = sections.map((section, index) => (
    <React.Fragment key={section.id}>
      {index > 0 ? <Divider /> : null}
      {section.items.map(({ id, label: itemLabel, icon: Icon, to, selected, soon }) =>
        soon ? (
          <RowTooltip key={id} label={SOON_TOOLTIP} position="right">
            <DisabledItem aria-disabled="true">
              <Icon size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />
              <span>{itemLabel}</span>
              <Soon label={soonLabel} />
            </DisabledItem>
          </RowTooltip>
        ) : (
          <ItemLink
            key={id}
            to={to}
            $selected={Boolean(selected)}
            aria-current={selected ? "page" : undefined}
            tabIndex={peek && !open ? -1 : undefined}
            onClick={docked ? undefined : onClose}
          >
            <Icon size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />
            <span>{itemLabel}</span>
          </ItemLink>
        ),
      )}
    </React.Fragment>
  ));

  if (peek) {
    return (
      <PeekDrawer
        aria-label={label}
        aria-hidden={!open}
        className={className}
        $open={open}
        $docked={docked}
        data-testid="nav-drawer-peek"
        data-state={!open ? "collapsed" : docked ? "pinned" : "preview"}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
      >
        <PanelHead>
          <PanelLabel>{label}</PanelLabel>
          {onPinToggle ? (
            <PinButton type="button" $pinned={docked} aria-label={pinLabel} title={pinLabel} tabIndex={open ? 0 : -1} onClick={onPinToggle}>
              <Pin size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} aria-hidden="true" />
            </PinButton>
          ) : null}
        </PanelHead>
        {rows}
        {footer ? <Footer>{footer}</Footer> : null}
      </PeekDrawer>
    );
  }

  return (
    <>
      <Scrim $closing={closing} data-testid="nav-drawer-scrim" aria-hidden="true" onClick={onClose} />
      <ModalDrawer
        $closing={closing}
        aria-label={label}
        className={className}
        ref={dialog.dialogRef as React.Ref<HTMLElement>}
        onKeyDown={dialog.onKeyDown}
      >
        <Heading ref={dialog.titleRef} tabIndex={-1}>
          {label}
        </Heading>
        {rows}
        {footer ? <Footer>{footer}</Footer> : null}
      </ModalDrawer>
    </>
  );
};

export default NavDrawer;
